import * as t from '$trans';
import type { Field, FormState, TypeToFields } from '@jmvdevelop/svelte-form';
import { createProperties, createRoot, Form } from '@jmvdevelop/svelte-form';
import { get } from 'svelte/store';
import type { ShareableContainer } from '$lib/container/shareableContainer';
import { getMessageFromError } from '$lib/utils/error';
import { mergeObject } from '$lib/utils/mergeObject';
import { pipe } from '$lib/utils/pipe';
import { fail, ok, type Result } from '$lib/utils/result';
import { temporizePromise } from '$lib/utils/temporizePromise';
import { validateEmail } from '$lib/validator/email';
import { lengthIsBetween } from '$lib/validator/isBetween';
import { notBlankAndNotNull } from '$lib/validator/required';
import {
    type CurrentUser_MeFragment,
    SendGoafricaContactMutationDocument,
} from '$gql/graphql';

export type FormData = {
    email: string | null;
    lastname: string | null;
    firstname: string | null;
    phone: string | null;
    companyName: string | null;
    message: string;
    subject: string | null;
    to: string | null;
    reportType: string | null;
    relatedCompanyId: number | null;
    relatedCategoryId: number | null;
    relatedFeedPostId: number | null;
};

export type Fields<TFormValues> = TypeToFields<TFormValues, FormData>;

export const empty: FormData = {
    email: null,
    lastname: null,
    firstname: null,
    phone: null,
    companyName: null,
    message: '',
    subject: null,
    to: null,
    reportType: null,
    relatedCompanyId: null,
    relatedCategoryId: null,
    relatedFeedPostId: null,
};

export function partialFromCurrentUser(currentUser: CurrentUser_MeFragment | null) {
    return {
        email: currentUser?.email ?? null,
        lastname: currentUser?.lastname ?? null,
        firstname: currentUser?.firstname ?? null,
        phone: currentUser?.firstPhoneNumber ?? null,
    };
}

export function createFields<TValues>(self: Field<TValues, FormData>): Fields<TValues> {
    return {
        self: self,
        ...createProperties(self, [
            'email',
            'lastname',
            'firstname',
            'phone',
            'companyName',
            'message',
            'subject',
            'to',
            'relatedCompanyId',
            'relatedCategoryId',
            'relatedFeedPostId',
            'reportType',
        ] as const),
    };
}

export function validate(
    form: Form<FormData, string>,
    fields: Fields<FormData> | null,
    state: FormState<FormData, string>,
    options: {
        withCompanyNameRequired: boolean;
        withSubject: boolean;
        withPhoneRequired: boolean;
    },
) {
    const values = state.values;

    return pipe(
        state,
        options.withCompanyNameRequired
            ? notBlankAndNotNull(values.companyName, {
                  form,
                  field: fields?.companyName,
                  message: 'Vous devez saisir le nom de votre société.',
              })
            : (s) => s,
        options.withSubject
            ? notBlankAndNotNull(values.subject, {
                  form,
                  field: fields?.subject,
                  message: 'Vous devez choisir un sujet',
              })
            : (s) => s,
        options.withSubject
            ? notBlankAndNotNull(values.to, {
                  form,
                  field: fields?.to,
                  message: 'Vous devez choisir un destinataire',
              })
            : (s) => s,
        options.withPhoneRequired
            ? notBlankAndNotNull(values.phone, {
                  form,
                  field: fields?.phone,
                  message: 'Vous devez saisir votre numéro de téléphone',
              })
            : (s) => s,
        lengthIsBetween(values.firstname, { form, max: 255, field: fields?.firstname }),
        lengthIsBetween(values.lastname, { form, max: 255, field: fields?.lastname }),
        lengthIsBetween(values.phone, { form, max: 50, field: fields?.phone }),
        lengthIsBetween(values.message, {
            form,
            min: 10,
            max: 10000,
            field: fields?.message,
        }),
        validateEmail(values.email, {
            form,
            field: fields?.email,
            notBlank: true,
            message: 'Vous devez saisir une adresse email valide',
        }),
        values.subject === 'report_feed_post'
            ? notBlankAndNotNull(values.reportType, {
                  form,
                  field: fields?.reportType,
                  message: 'Obligatoire.',
              })
            : (s) => s,
    );
}

export function createForm(
    id: string,
    initialValues: Partial<FormData> = empty,
    options: {
        withCompanyNameRequired: boolean;
        withPhoneRequired: boolean;
        withSubject: boolean;
    },
): [Form<FormData, string>, Fields<FormData>] {
    const fields = createFields<FormData>(createRoot<FormData>());
    const form = new Form<FormData, string>(
        { initialValues: mergeObject(empty, initialValues) },
        id,
    );

    form.addValidator((state) => {
        return validate(form, fields, state, {
            withCompanyNameRequired: options.withCompanyNameRequired,
            withPhoneRequired: options.withPhoneRequired,
            withSubject: options.withSubject,
        });
    });

    return [form, fields];
}

type OnSubmit = (form: Form<FormData, string>) => Promise<Result<number, string>>;

export const createOnSubmit: (
    container: Pick<
        ShareableContainer,
        'apollo' | 'datalayer' | 'toaster' | 'recaptchaHelper' | 'lang'
    >,
) => OnSubmit =
    ({ apollo, datalayer, toaster, recaptchaHelper, lang }) =>
    (form) => {
        return temporizePromise(
            500,
            (async () => {
                try {
                    const recaptcha = await recaptchaHelper().executeRecaptcha('submit');

                    const values = form.getState().values;

                    const gclid = getGclidFromWindow();

                    const result = await apollo().mutate({
                        mutation: SendGoafricaContactMutationDocument,
                        variables: {
                            email: values.email,
                            gclid,
                            firstname: values.firstname,
                            lastname: values.lastname,
                            message: values.message,
                            phone: values.phone,
                            referrer: window?.document.location.href ?? null,
                            subject: values.subject,
                            to: values.to,
                            relatedCompanyId: values.relatedCompanyId,
                            relatedCategoryId: values.relatedCategoryId,
                            relatedFeedPostId: values.relatedFeedPostId,
                            reportType: values.reportType,
                            recaptcha,
                        },
                    });

                    if (result.errors != null || result.data == null) {
                        return fail(getMessageFromError(result.errors));
                    }

                    const contact = result.data.sendGoafricaContact;
                    datalayer()?.push({
                        event: 'goafrica_contact.submit',
                        pathname: window?.location.pathname ?? null,
                        gclid,
                        goafricaContact: {
                            id: contact.id,
                            email: contact.email,
                            phone: contact.phone,
                        },
                    });

                    toaster().helpers.addToast({
                        data: {
                            type: 'success',
                            message: t.page_contact.contact_toast_success({}, get(lang)),
                        },
                    });

                    return ok(contact.id);
                } catch (e) {
                    return fail(getMessageFromError(e));
                }
            })(),
        );
    };

function getGclidFromWindow(): string | null {
    if (typeof window === 'undefined') {
        return null;
    }

    const searchParams = new URLSearchParams(window.document.location.search);
    return searchParams.has('gclid') ? searchParams.get('gclid') : null;
}
